import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EntityMultiSelect,
  type EntityMultiSelectOption,
} from '@/components/entity-multi-select/entity-multi-select';
import { useGetOfficeDoctorAttendingsQuery } from '@/hooks/queries/use-get-office-doctor-attendings';
import { doctorFullnameTitleTrad } from '@/utils/doctor';

const DOCTOR_ATTENDINGS_PAGE_SIZE = 200;

type Props = {
  officeId: string;
  value: string[] | undefined;
  onValueChange: (doctorAttendingIds: string[] | undefined) => void;
};

/**
 * Attending-doctor multi-select for an office. Thin wrapper around
 * {@link EntityMultiSelect} in split mode: `undefined` means "all doctors"
 * and an emptied selection normalizes back to `undefined`.
 *
 * @param props.officeId - the office whose attendings are selectable
 * @param props.value - selected doctor ids, or `undefined` for all
 * @param props.onValueChange - called with the next selection
 * @returns the doctor multi-select
 */
export const SelectDoctorAttendingMulti = ({
  officeId,
  value,
  onValueChange,
}: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetOfficeDoctorAttendingsQuery({
    page: 1,
    per_page: DOCTOR_ATTENDINGS_PAGE_SIZE,
    officeId,
    officeRequired: true,
  });

  const options = useMemo<EntityMultiSelectOption[]>(() => {
    return (data?.data ?? []).map((item) => {
      const fullnameTrad = doctorFullnameTitleTrad(item);
      return {
        id: item.id,
        label: t(fullnameTrad.key, fullnameTrad.params),
      };
    });
  }, [data, t]);

  return (
    <EntityMultiSelect
      mode="split"
      options={options}
      label={t('export-accounting-transfer-order-form.doctor-attending.label')}
      searchable
      loading={isLoading}
      className="w-full"
      value={value}
      onValueChange={(next) =>
        onValueChange(
          next !== undefined && next.length === 0 ? undefined : next,
        )
      }
    />
  );
};
